import React, { useId } from 'react';
import ReactModal, { OnAfterOpenCallback } from 'react-modal';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { FocusTrap } from 'focus-trap-react';
import useWindowSize from '@hooks/use-window-size';
import { useFocusRestore } from '@hooks/a11y/use-focus-restore';
import Button from '@components/atoms/button/button';
import Icon from '../icon/icon';
import parentStyles from './dialog.module.scss';

const defaultStyles: ReactModal.Styles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.32)',
    zIndex: 1000,
    maxWidth: '100%',
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '0%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '100%',
    maxHeight: '100%',
    display: 'flex',
    position: 'absolute',
    borderRadius: '1px',
    overflowY: 'auto',
  },
};

export type DialogProps = {
  isOpen: boolean;
  closeDialog?: (arg: boolean) => void;
  hasCloseIcon?: boolean;
  onAfterOpen?: OnAfterOpenCallback;
  onAfterClose?: OnAfterOpenCallback;
  contentLabel?: string;
  styles?: ReactModal.Styles;
  focusTrapActive?: boolean;
  hasCloseTxt?: boolean;
  fullScreenOnMobile?: boolean;
  customClass?: string;
  dataCyIdentifierForContainer?: string;
  parentSelector?: () => HTMLElement;
  shouldCloseOnOverlayClick?: boolean;
  children: React.ReactNode | ((modalDialogTitleId: string) => React.ReactNode);
};

const Dialog: React.FC<DialogProps> = (props): React.JSX.Element => {
  const {
    isOpen,
    onAfterOpen,
    onAfterClose,
    children,
    styles,
    hasCloseIcon,
    closeDialog,
    fullScreenOnMobile = true,
    focusTrapActive = true,
    hasCloseTxt = false,
    customClass,
    dataCyIdentifierForContainer = 'modal_inner_container',
    contentLabel,
    parentSelector,
    shouldCloseOnOverlayClick = true,
  } = props;

  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');
  const { isWiderThanMd } = useWindowSize();
  const modalDialogTitleId = useId();

  // Automatically restore focus when modal closes
  useFocusRestore(isOpen);

  const dialogContent = typeof children === 'function' ? children(modalDialogTitleId) : children;
  const ariaProps =
    typeof children === 'function'
      ? { aria: { labelledby: modalDialogTitleId } }
      : { contentLabel: contentLabel || tA11y('A11Y.MODAL_DIALOG') };

  const combinedStyles: ReactModal.Styles = {
    overlay: {
      ...defaultStyles.overlay,
      ...styles?.overlay,
    },
    content: {
      ...defaultStyles.content,
      ...styles?.content,
    },
  };

  const onClose = () => {
    closeDialog(false);
  };

  return (
    <>
      {combinedStyles && (
        <ReactModal
          isOpen={isOpen}
          onAfterOpen={onAfterOpen}
          onRequestClose={onClose}
          onAfterClose={onAfterClose}
          shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
          bodyOpenClassName="scroll-lock"
          style={{
            ...combinedStyles,
            content: {
              ...combinedStyles.content,
              borderRadius: isWiderThanMd ? '1px' : 0,
            },
          }}
          className={clsx(
            parentStyles.popup,
            { [parentStyles['popup--mobileFullScreen']]: fullScreenOnMobile },
            customClass,
          )}
          parentSelector={parentSelector}
          ariaHideApp={false}
          closeTimeoutMS={300}
          {...ariaProps}
        >
          <FocusTrap active={isOpen && focusTrapActive}>
            {hasCloseIcon ? (
              <div
                className={clsx('vc-modal__innerContainer', {
                  'vc-modal__innerContainer__fullScreenOnMobile': fullScreenOnMobile,
                })}
                data-cy={dataCyIdentifierForContainer}
              >
                <Button
                  disableDefaultStyling
                  aria-label={t('MODAL.BUTTON.CLOSE')}
                  className={parentStyles.closeBtn}
                  onClick={onClose}
                  tabIndex={0}
                  data-cy="close_modal_button"
                >
                  <span className={clsx(!hasCloseTxt && 'visuallyhidden', parentStyles.closeTxt)}>
                    {t('MODAL.BUTTON.CLOSE')}
                  </span>
                  <Icon
                    name="close-default"
                    className={parentStyles.closeIcon}
                    ariaHidden
                  />
                </Button>
                {dialogContent}
              </div>
            ) : (
              dialogContent
            )}
          </FocusTrap>
        </ReactModal>
      )}
    </>
  );
};

export default Dialog;
